import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  BarChart2,
  Clock,
  Folder,
  Home,
  LoaderCircle,
  Menu,
  Settings,
  TriangleAlert,
} from "lucide-react";

import { DEFAULT_ROUTE, PRIMARY_ROUTES } from "@zentrack/config";
import { activeSessionElapsedSeconds, formatDurationCompact } from "@zentrack/domain";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Toaster,
  toast,
} from "@zentrack/ui";

import { useWebApp } from "./app-provider";
import { useOnlineStatus, usePwaUpdate } from "../lib/pwa";

const ROUTE_ICONS: Record<string, React.ElementType> = {
  "/overview": Home,
  "/clients": Folder,
  "/history": Clock,
  "/reports": BarChart2,
  "/settings": Settings,
};

function NavLink({ href, label, onNavigate }: { href: string; label: string; onNavigate?: () => void }) {
  const location = useLocation();
  const Icon = ROUTE_ICONS[href];
  const isActive = location.pathname === href;

  return (
    <Link
      to={href}
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
        isActive
          ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
          : "text-sidebar-foreground/60 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground",
      )}
    >
      {Icon ? <Icon className="size-4 shrink-0" /> : null}
      <span>{label}</span>
    </Link>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { snapshot } = useWebApp();
  const location = useLocation();
  const activeSession = snapshot?.activeSession ?? null;
  const isPaused = Boolean(activeSession?.pausedAt);
  const showSidebarTimer = location.pathname !== "/overview" && Boolean(activeSession);
  const elapsed = activeSession
    ? formatDurationCompact(
        activeSessionElapsedSeconds(
          activeSession.startTime,
          new Date(),
          activeSession.pausedDurationSeconds,
          activeSession.pausedAt,
        ),
      )
    : null;

  return (
    <div className="flex h-full flex-col gap-1 p-3">
      <nav className="flex flex-1 flex-col gap-0.5">
        {PRIMARY_ROUTES.map((route) => (
          <NavLink href={route.href} key={route.href} label={route.label} onNavigate={onNavigate} />
        ))}
      </nav>

      {showSidebarTimer ? (
        <div className="mt-auto flex flex-col gap-2 border-t border-sidebar-border pt-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Timer
            </span>
            <Badge tone={isPaused ? "neutral" : "accent"}>{isPaused ? "Paused" : "Running"}</Badge>
          </div>
          <p className="truncate text-sm font-medium text-foreground">
            {snapshot?.tray.projectLabel ?? "Unknown project"}
          </p>
          <p className="text-xs text-muted-foreground">{elapsed}</p>
          <Button
            render={<Link to="/overview" />}
            size="sm"
            variant="ghost"
            className="mt-1 justify-start px-0 text-xs"
          >
            ← Back to overview
          </Button>
        </div>
      ) : (
        <p className="mt-auto select-none pb-1 text-[11px] font-medium text-sidebar-foreground/30">
          ZenTrack
        </p>
      )}
    </div>
  );
}

function OfflineBanner() {
  const online = useOnlineStatus();

  if (online) {
    return null;
  }

  return (
    <Banner
      tone="warning"
      title="You're offline"
      message="Local tracking, history, and reports keep working. Reconnect to get the latest app shell."
      className="mb-4"
    />
  );
}

function UpdatePrompt() {
  const update = usePwaUpdate();

  useEffect(() => {
    if (!update?.offlineReady) {
      return;
    }

    toast("App is ready to work offline.", "success");
  }, [update?.offlineReady]);

  if (!update?.needRefresh) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed bottom-5 left-5 z-50">
      <div className="pointer-events-auto flex w-[320px] max-w-[calc(100vw-2.5rem)] items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm shadow-lg">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" />
        <div className="flex flex-1 flex-col gap-2">
          <p className="font-medium text-foreground">A new version is available</p>
          <p className="text-muted-foreground">Reload to update ZenTrack.</p>
          <Button
            onClick={() => void update.updateServiceWorker()}
            size="sm"
            type="button"
          >
            Reload to update
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AppShell() {
  const { api, errorMessage, isLoading, snapshot } = useWebApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const lastPersistedRoute = useRef<string | null>(null);

  // Persist the last route so the app can restore it after relaunch.
  useEffect(() => {
    if (!snapshot) {
      return;
    }

    if (location.pathname === "/") {
      return;
    }

    if (lastPersistedRoute.current === location.pathname) {
      return;
    }
    lastPersistedRoute.current = location.pathname;

    void api.settings
      .update({ lastRoute: location.pathname as (typeof DEFAULT_ROUTE) })
      .catch(() => {});
  }, [api, location.pathname, snapshot]);

  if (isLoading && !snapshot) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <LoaderCircle className="size-6 animate-spin" />
            </div>
            <CardTitle>Loading ZenTrack</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-6 text-muted-foreground">
              Opening your local workspace and timer state.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!snapshot && errorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex size-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <Activity className="size-6" />
            </div>
            <CardTitle>Could not load ZenTrack</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Banner message={errorMessage} tone="error" />
            <p className="text-sm leading-6 text-muted-foreground">
              The app could not open its local database. Reloading the page may help.
            </p>
            <Button onClick={() => window.location.reload()} type="button" variant="outline" className="w-fit">
              Reload
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!snapshot) {
    return null;
  }

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-full max-w-[220px] shrink-0 overflow-hidden border-r border-border bg-sidebar md:block">
        <SidebarContent />
      </aside>

      {/* Mobile nav */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-[260px] p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
          </SheetHeader>
          <SidebarContent onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="min-h-screen flex-1 px-4 py-4 pb-24 md:px-7 md:py-6 md:pb-6">
        {/* Mobile top bar */}
        <div className="mb-4 flex items-center justify-between md:hidden">
          <Button
            aria-label="Open navigation"
            onClick={() => setMobileNavOpen(true)}
            size="icon-sm"
            type="button"
            variant="outline"
          >
            <Menu className="size-4" />
          </Button>
          <span className="text-sm font-medium">ZenTrack</span>
          <span className="w-7" />
        </div>

        <OfflineBanner />
        {errorMessage ? <Banner className="mb-4" message={errorMessage} tone="error" /> : null}
        <div className="mx-auto max-w-[1080px]">
          <Outlet />
        </div>
      </main>

      <UpdatePrompt />
      <Toaster />
    </div>
  );
}
